/**
 * Étape 3 — Sa journée en stage.
 *
 * Les activités, la natation, et l'inclusion. L'inclusion était une étape à
 * part, en dernière position, que plus de neuf familles sur dix traversaient
 * sans rien y cocher — et les autres y arrivaient épuisées, au moment précis où
 * l'on a le plus besoin de ce qu'elles ont à dire.
 *
 * Elle devient ici une question parmi les autres, qui se déplie quand elle
 * concerne l'enfant. Son contenu, lui, n'a pas été allégé : c'est ce qui permet
 * d'accueillir, pas un questionnaire administratif.
 */

import { Bloc, Case, Choix, Info, Navigation, ZoneTexte } from './champs';
import { NIVEAUX_NATATION, PHRASE_NATATION } from './fiche';
import type { Accompagnement, Erreurs, NiveauNatation, VieEnStage } from './fiche';

/*
  Les précisions sont indexées par niveau, comme les phrases : en ajouter un
  sans l'ajouter aux deux endroits ne compile pas. Elles ne nomment ni « seul »
  ni « seule » — une fiche sur deux concerne une fille, et le masculin
  générique sonne faux dès qu'on parle de SON enfant.
*/
const PRECISIONS: Record<NiveauNatation, string> = {
  'pas du tout': 'Ne se déplace pas dans l’eau sans aide.',
  difficilement: 'Quelques mètres, avec appui ou surveillance rapprochée.',
  bien: 'Nage une longueur en autonomie, en petite profondeur.',
  'très bien': 'À l’aise, y compris en grande profondeur.',
};

const ACCOMPAGNEMENTS: { valeur: Accompagnement; libelle: string }[] = [
  { valeur: 'yes', libelle: 'Oui, en permanence' },
  { valeur: 'sometimes', libelle: 'Par moments' },
  { valeur: 'no', libelle: 'Non' },
];

const EtapeVie = ({
  valeur,
  onChange,
  erreurs,
  onPrecedent,
  onSuivant,
  prenom,
}: {
  valeur: VieEnStage;
  onChange: (v: VieEnStage) => void;
  erreurs: Erreurs;
  onPrecedent: () => void;
  onSuivant: () => void;
  prenom: string;
}) => {
  const maj = (champs: Partial<VieEnStage>) => onChange({ ...valeur, ...champs });
  const enfant = prenom.trim() || 'votre enfant';
  const eauDelicate = valeur.natation === 'pas du tout' || valeur.natation === 'difficilement';

  const niveaux = NIVEAUX_NATATION.map((v) => ({
    valeur: v,
    libelle: PHRASE_NATATION[v](enfant),
    detail: PRECISIONS[v],
  }));

  return (
    <div className="space-y-8">
      <Bloc titre="Les activités">
        <Case
          coche={valeur.participeATout}
          onChange={(v) => maj({ participeATout: v })}
          label={`${enfant} peut participer à toutes les activités proposées`}
          aide="Sport, sorties, psychomotricité, cuisine, piscine…"
        />

        {!valeur.participeATout && (
          <div className="border-l-2 border-primary-100 pl-4">
            <ZoneTexte
              label={`Ce que ${enfant} ne peut pas faire, et pourquoi`}
              requis
              valeur={valeur.restrictions}
              onChange={(v) => maj({ restrictions: v })}
              erreur={erreurs.restrictions}
              placeholder="Pas de course à pied (asthme d’effort), pas de piscine pendant le traitement…"
              lignes={3}
            />
          </div>
        )}
      </Bloc>

      <Bloc
        titre="L’eau"
        intro="Nous allons à la piscine et, l’été, près de points d’eau. Ce niveau détermine l’encadrement."
      >
        <Choix
          label="Quel est son niveau de natation&nbsp;?"
          requis
          valeur={valeur.natation || null}
          onChange={(v) => maj({ natation: v })}
          options={niveaux}
          erreur={erreurs.natation}
          colonnes={2}
          pourquoi="L’équipe organise les groupes de baignade à partir de cette réponse. En cas de doute, choisissez le niveau inférieur : un enfant surestimé est un enfant en danger."
        />

        {eauDelicate && (
          <Case
            coche={valeur.peurDeLEau}
            onChange={(v) => maj({ peurDeLEau: v })}
            label={`${enfant} a peur de l’eau`}
            aide="Nous adaptons l’accompagnement, sans forcer."
          />
        )}
      </Bloc>

      <Bloc titre="Besoins particuliers">
        <Case
          coche={valeur.besoinsSpecifiques}
          onChange={(v) => maj({ besoinsSpecifiques: v })}
          label={`${enfant} a un handicap, un trouble ou des besoins spécifiques`}
          aide="Autisme, TDAH, dys-, handicap moteur ou sensoriel, trouble du comportement…"
        />

        {valeur.besoinsSpecifiques && (
          <div className="space-y-5 border-l-2 border-primary-100 pl-4">
            <Info>
              Ces questions servent à évaluer les moyens à mettre en place, pas à décider sur la
              base d’un diagnostic. Plus vous êtes précis, mieux nous préparons l’accueil — et
              plus tôt nous vous disons franchement ce que nous pouvons assurer.
            </Info>

            <ZoneTexte
              label="Quelle situation, quel trouble, quels besoins"
              requis
              valeur={valeur.situation}
              onChange={(v) => maj({ situation: v })}
              erreur={erreurs.situation}
              lignes={3}
            />
            <ZoneTexte
              label="Ce que cela change dans une journée de groupe"
              facultatif
              valeur={valeur.impact}
              onChange={(v) => maj({ impact: v })}
              placeholder="Repas, bruit, transitions, sieste, déplacements…"
              lignes={3}
            />

            <Choix
              label="Un accompagnement dédié est-il nécessaire&nbsp;?"
              valeur={valeur.accompagnementDedie || null}
              onChange={(v) => maj({ accompagnementDedie: v })}
              options={ACCOMPAGNEMENTS}
            />

            {(valeur.accompagnementDedie === 'yes' || valeur.accompagnementDedie === 'sometimes') && (
              <ZoneTexte
                label="À quels moments, et pour quoi faire"
                facultatif
                valeur={valeur.accompagnementDetails}
                onChange={(v) => maj({ accompagnementDetails: v })}
                lignes={3}
              />
            )}

            <ZoneTexte
              label="Ce qui fonctionne avec lui"
              facultatif
              valeur={valeur.strategies}
              onChange={(v) => maj({ strategies: v })}
              placeholder="Prévenir avant un changement, proposer un coin calme, reformuler les consignes…"
              lignes={3}
            />
            <ZoneTexte
              label="Matériel ou aides à prévoir"
              facultatif
              valeur={valeur.materiel}
              onChange={(v) => maj({ materiel: v })}
              placeholder="Fauteuil, appareil auditif, pictogrammes, casque anti-bruit…"
              lignes={2}
            />
            <ZoneTexte
              label={`Comment on voit que ${enfant} ne va pas bien`}
              facultatif
              valeur={valeur.signauxDeStress}
              onChange={(v) => maj({ signauxDeStress: v })}
              placeholder="Se tait, s’isole, se bouche les oreilles, s’agite…"
              lignes={2}
            />
            <ZoneTexte
              label="Ce qui lui plaît et ce qui lui réussit"
              facultatif
              valeur={valeur.forces}
              onChange={(v) => maj({ forces: v })}
              aide="Ce n’est pas une formalité : c’est souvent par là que l’équipe entre en lien."
              lignes={2}
            />
            <ZoneTexte
              label="Expériences précédentes en collectivité"
              facultatif
              valeur={valeur.experiences}
              onChange={(v) => maj({ experiences: v })}
              placeholder="Ce qui s’est bien passé, ce qui s’est mal passé, et pourquoi."
              lignes={3}
            />
          </div>
        )}
      </Bloc>

      <Bloc titre="Autre chose ?">
        <ZoneTexte
          label={`Ce que nous devrions savoir sur ${enfant}`}
          facultatif
          valeur={valeur.autresInfos}
          onChange={(v) => maj({ autresInfos: v })}
          placeholder="Séparation récente, deuil, arrivée d’un bébé, grande timidité, peur du noir…"
          lignes={3}
        />
      </Bloc>

      <Navigation onPrecedent={onPrecedent} onSuivant={onSuivant} />
    </div>
  );
};

export default EtapeVie;
